import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PublicShell, Loading } from "@/components/shared";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FEATURES = {
  free: ["Selected starter resources", "Resource previews", "Free community area", "Newsletter subscription", "AI tool directory"],
  premium: ["Everything in Free", "Full playbook library", "AI prompt vault", "Downloadable templates", "Saved workspace & collections", "Premium community rooms"],
  pro: ["Everything in Premium", "Complete business academy", "All Growth Packs", "Live sessions with Shubham", "Priority resource requests", "Weekly implementation"],
};

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true); s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Pricing() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [region, setRegion] = useState("india");
  const [interval, setInterval] = useState("monthly");
  const [busy, setBusy] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["plans"], queryFn: async () => (await api.get("/payments/plans")).data });

  const price = (plan) => {
    const p = data.plans[plan][region === "india" ? "inr" : "usd"][interval];
    return region === "india" ? `₹${p.toLocaleString("en-IN")}` : `$${p}`;
  };

  const upgrade = async (planId) => {
    if (!user) return navigate("/register");
    setBusy(planId);
    try {
      if (region === "international") {
        const { data: res } = await api.post("/payments/stripe/checkout", { plan_id: planId, interval, origin_url: window.location.origin });
        window.location.href = res.checkout_url;
      } else {
        if (!data.razorpay_enabled) { toast.error("Razorpay isn't configured yet. Please try the International (Stripe) option or contact us."); setBusy(""); return; }
        const ok = await loadRazorpay();
        if (!ok) { toast.error("Could not load Razorpay."); setBusy(""); return; }
        const { data: order } = await api.post("/payments/razorpay/order", { plan_id: planId, interval });
        const rzp = new window.Razorpay({
          key: order.key_id, amount: order.amount, currency: order.currency,
          name: "Shubham Bapna", description: `${data.plans[planId].name} (${interval})`, order_id: order.order_id,
          prefill: { name: order.name, email: order.email },
          theme: { color: "#059669" },
          handler: async (resp) => {
            try { await api.post("/payments/razorpay/verify", resp); await refreshUser(); toast.success("Membership activated!"); navigate("/dashboard"); }
            catch { toast.error("Payment verification failed."); }
          },
        });
        rzp.open();
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout"); }
    finally { setBusy(""); }
  };

  if (isLoading) return <PublicShell><Loading /></PublicShell>;

  const Tier = ({ id, name, sub, highlight }) => (
    <div className={`relative flex flex-col rounded-2xl border p-8 ${highlight ? "border-emerald bg-navy text-white shadow-xl" : "border-slate-200 bg-white"}`} data-testid={`pricing-${id}`}>
      {highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-emerald px-3 py-1 text-xs font-bold text-white">Most popular</span>}
      <h3 className={`font-heading text-xl font-bold ${highlight ? "text-white" : "text-navy"}`}>{name}</h3>
      <p className={`mt-1 text-sm ${highlight ? "text-slate-400" : "text-slate-500"}`}>{sub}</p>
      <div className="mt-5">
        {id === "free" ? <p className={`font-heading text-4xl font-bold ${highlight ? "text-white" : "text-navy"}`}>₹0</p> : (
          <p className={`font-heading text-4xl font-bold ${highlight ? "text-white" : "text-navy"}`}>{price(id)}<span className={`text-base font-medium ${highlight ? "text-slate-400" : "text-slate-500"}`}>/{interval === "monthly" ? "mo" : "yr"}</span></p>
        )}
      </div>
      <ul className="mt-6 flex-1 space-y-3">
        {FEATURES[id].map((f) => (
          <li key={f} className={`flex items-start gap-2.5 text-sm ${highlight ? "text-slate-300" : "text-slate-600"}`}><Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald" /> {f}</li>
        ))}
      </ul>
      <Button
        onClick={() => id === "free" ? navigate(user ? "/dashboard" : "/register") : upgrade(id)}
        disabled={busy === id || (user && (user.tier === id || (id === "premium" && user.tier === "pro")))}
        data-testid={`pricing-cta-${id}`}
        className={`mt-8 h-11 w-full rounded-md font-semibold ${highlight ? "bg-emerald text-white hover:bg-emerald-700" : id === "free" ? "bg-slate-100 text-navy hover:bg-slate-200" : "bg-navy text-white hover:bg-navysurface"}`}>
        {busy === id ? "Processing…" : (user && user.tier === id) ? "Current plan" : id === "free" ? "Get started" : `Choose ${name}`}
      </Button>
    </div>
  );

  return (
    <PublicShell>
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-3xl px-5 py-16 text-center lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald">Pricing</p>
          <h1 className="mt-3 font-heading text-4xl font-bold tracking-tight text-navy sm:text-5xl">Simple plans that grow with you</h1>
          <p className="mt-3 text-slate-600">Start free. Upgrade when you're ready for the full library, courses and premium community.</p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1" data-testid="region-toggle">
              {[["india", "🇮🇳 India (₹)"], ["international", "🌍 International ($)"]].map(([v, l]) => (
                <button key={v} onClick={() => setRegion(v)} data-testid={`region-${v}`}
                  className={`rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${region === v ? "bg-navy text-white" : "text-slate-600"}`}>{l}</button>
              ))}
            </div>
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1" data-testid="interval-toggle">
              {[["monthly", "Monthly"], ["annual", "Annual · save"]].map(([v, l]) => (
                <button key={v} onClick={() => setInterval(v)} data-testid={`interval-${v}`}
                  className={`rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${interval === v ? "bg-emerald text-white" : "text-slate-600"}`}>{l}</button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-16 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-3">
          <Tier id="free" name="Free" sub="For getting started" />
          <Tier id="premium" name="Premium" sub="For active builders" />
          <Tier id="pro" name="Pro" sub="For serious operators" highlight />
        </div>
        <p className="mt-10 text-center text-sm text-slate-500">
          <Sparkles className="mr-1 inline h-4 w-4 text-emerald" /> Indian customers pay via Razorpay (UPI/cards). International customers pay via Stripe. Test card: 4242 4242 4242 4242.
        </p>
      </section>
    </PublicShell>
  );
}
